/**
 * Phase 7 Plan 03 — Blob download triggers (4 datasets) per CONTEXT D-150 + D-155.
 *
 * The /api/admin/exports/* endpoints return BINARY bodies (XLSX or PDF) — they
 * cannot be consumed via apiGet (which calls .json()). Use raw fetch with
 * credentials:'include' so the pm_session cookie travels (T-7-03-01 mitigation —
 * without this the server returns 401 silently because pm_session is HttpOnly),
 * then convert response.blob() → temporary <a download> anchor and click.
 *
 * On non-2xx, attempt to parse a {code: ERR_*} envelope (D-19 server shape) and
 * throw Error(code) so callers can route the code through FormattedMessage / the
 * i18n bundle. We never log the response body (CP4 discipline).
 *
 * Filenames come from the server's Content-Disposition header (server-controlled
 * per D-157 — `<dataset>-<YYYYMMDD-HHMM>.<ext>`); we parse the regex value and
 * fall back to "export.bin" only on truly malformed responses.
 */

async function downloadBlob(path: string): Promise<void> {
  // Audit W4-004 — distinguish pre-response transport failure (DNS, offline,
  // brief Caddy 502 during deploy, browser network shutdown) from a received
  // non-2xx response. Pre-fix: a TypeError "Failed to fetch" propagated as
  // err.message and rendered via <FormattedMessage id={err.message}> as the
  // raw English literal "Failed to fetch" — non-localized, not in the
  // ERR_* catalog. Now: catch the fetch rejection and throw a stable
  // ERR_NETWORK code (catalog: "Could not reach the server. Please check
  // your connection." / Arabic equivalent), letting ExportsPage render a
  // bilingual recoverable-error banner with a localized retry message.
  let response: Response;
  try {
    response = await fetch(path, { credentials: "include" });
  } catch {
    throw new Error("ERR_NETWORK");
  }
  if (!response.ok) {
    let code = "ERR_GENERIC";
    try {
      const body = (await response.json()) as { code?: string };
      if (body.code) code = body.code;
    } catch {
      // Opaque / non-JSON body; keep ERR_GENERIC.
    }
    throw new Error(code);
  }
  const cd = response.headers.get("Content-Disposition") ?? "";
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? "export.bin";
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const downloadAssetRoster = (): Promise<void> =>
  downloadBlob("/api/admin/exports/asset_roster");

export const downloadCurrentState = (): Promise<void> =>
  downloadBlob("/api/admin/exports/current_state");

export const downloadInspectionLog = (qs: string): Promise<void> =>
  downloadBlob(`/api/admin/exports/inspection_log${qs}`);

export const downloadPerAssetPdf = (assetId: number): Promise<void> =>
  downloadBlob(`/api/admin/exports/per_asset_history_pdf?asset_id=${assetId}`);
